// CISA Known Exploited Vulnerabilities (KEV) Catalog integration
// Original API: https://www.cisa.gov/sites/default/files/feeds/known_exploited_vulnerabilities.json
// Proxied through our API to avoid CORS issues in browser
// Edge runtime compatible - uses fetch API only

// In the browser, go through our proxy API to avoid CORS issues.
// On the server (SSR / edge worker) there is no CORS restriction and relative
// URLs are invalid, so fetch the upstream CISA feed directly.
const KEV_PROXY_URL = '/api/tools/cve/kev';
const KEV_UPSTREAM_URL =
  'https://www.cisa.gov/sites/default/files/feeds/known_exploited_vulnerabilities.json';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface KEVEntry {
  cveID: string;
  vendorProject: string;
  product: string;
  vulnerabilityName: string;
  dateAdded: string;
  shortDescription: string;
  requiredAction: string;
  dueDate: string;
  knownRansomwareCampaignUse: string;
  notes?: string;
}

export interface KEVCatalog {
  title: string;
  catalogVersion: string;
  dateReleased: string;
  count: number;
  vulnerabilities: KEVEntry[];
}

export interface KEVStats {
  totalCount: number;
  recentlyAdded: KEVEntry[]; // Last 30 days
  byVendor: { vendor: string; count: number }[];
  ransomwareRelated: number;
}

// In-memory cache for edge runtime
const memoryCache: {
  data: KEVCatalog | null;
  timestamp: number | null;
} = {
  data: null,
  timestamp: null,
};

/**
 * Fetch CISA KEV catalog with caching
 * Uses in-memory cache for edge runtime compatibility
 */
export async function fetchKEVCatalog(forceRefresh = false): Promise<KEVCatalog | null> {
  try {
    // Check memory cache first
    if (!forceRefresh && memoryCache.data && memoryCache.timestamp) {
      const age = Date.now() - memoryCache.timestamp;
      if (age < CACHE_DURATION_MS) {
        return memoryCache.data;
      }
    }

    // Browser: relative proxy avoids CORS. Server: hit CISA directly
    // (relative URLs throw "Invalid URL" in the edge worker).
    const isServer = typeof window === 'undefined';
    const response = isServer
      ? await fetch(KEV_UPSTREAM_URL, {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'InventiveHQ-CVE-Lookup/1.0 (https://inventivehq.com)',
          },
        })
      : await fetch(KEV_PROXY_URL, {
          headers: {
            'Accept': 'application/json',
          },
        });

    if (!response.ok) {
      throw new Error(`KEV API returned ${response.status}`);
    }

    const data: KEVCatalog = await response.json();

    // Update memory cache
    memoryCache.data = data;
    memoryCache.timestamp = Date.now();

    return data;
  } catch (error) {
    console.error('Error fetching CISA KEV catalog:', error);
    // Return cached data if available, even if expired
    if (memoryCache.data) {
      return memoryCache.data;
    }
    return null;
  }
}

/**
 * Check if a CVE is in the KEV catalog
 */
export async function isKEVListed(cveId: string): Promise<KEVEntry | null> {
  const catalog = await fetchKEVCatalog();
  if (!catalog) return null;

  const normalizedCveId = cveId.toUpperCase();
  const entry = catalog.vulnerabilities.find(
    (vuln) => vuln.cveID.toUpperCase() === normalizedCveId
  );

  return entry || null;
}

/**
 * Get KEV statistics
 */
export async function getKEVStats(): Promise<KEVStats | null> {
  const catalog = await fetchKEVCatalog();
  if (!catalog) return null;

  // Calculate recently added (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentlyAdded = catalog.vulnerabilities
    .filter((vuln) => new Date(vuln.dateAdded) >= thirtyDaysAgo)
    .sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime())
    .slice(0, 10);

  // Count by vendor
  const vendorCounts = new Map<string, number>();
  catalog.vulnerabilities.forEach((vuln) => {
    const vendor = vuln.vendorProject;
    vendorCounts.set(vendor, (vendorCounts.get(vendor) || 0) + 1);
  });

  const byVendor = Array.from(vendorCounts.entries())
    .map(([vendor, count]) => ({ vendor, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Count ransomware-related
  const ransomwareRelated = catalog.vulnerabilities.filter(
    (vuln) => vuln.knownRansomwareCampaignUse === 'Known'
  ).length;

  return {
    totalCount: catalog.count,
    recentlyAdded,
    byVendor,
    ransomwareRelated,
  };
}

/**
 * Search KEV catalog by keyword
 */
export async function searchKEV(keyword: string): Promise<KEVEntry[]> {
  const catalog = await fetchKEVCatalog();
  if (!catalog) return [];

  const searchTerm = keyword.toLowerCase();

  return catalog.vulnerabilities.filter((vuln) => {
    return (
      vuln.cveID.toLowerCase().includes(searchTerm) ||
      vuln.vendorProject.toLowerCase().includes(searchTerm) ||
      vuln.product.toLowerCase().includes(searchTerm) ||
      vuln.vulnerabilityName.toLowerCase().includes(searchTerm) ||
      vuln.shortDescription.toLowerCase().includes(searchTerm)
    );
  });
}

/**
 * Filter CVE IDs that are in KEV catalog
 */
export async function filterKEVCVEs(cveIds: string[]): Promise<string[]> {
  const catalog = await fetchKEVCatalog();
  if (!catalog) return [];

  const kevCveIds = new Set(
    catalog.vulnerabilities.map((vuln) => vuln.cveID.toUpperCase())
  );

  return cveIds.filter((cveId) => kevCveIds.has(cveId.toUpperCase()));
}

/**
 * Get all KEV entries (for filtering)
 */
export async function getAllKEVEntries(): Promise<KEVEntry[]> {
  const catalog = await fetchKEVCatalog();
  return catalog?.vulnerabilities || [];
}

/**
 * Check if due date is approaching (within 7 days)
 */
export function isDueDateApproaching(dueDate: string): boolean {
  const due = new Date(dueDate);
  const now = new Date();
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

  return due <= sevenDaysFromNow && due >= now;
}

/**
 * Check if due date has passed
 */
export function isDueDatePassed(dueDate: string): boolean {
  const due = new Date(dueDate);
  const now = new Date();
  return due < now;
}
